import { useState, useRef, useEffect } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  Switch,
  Select,
  Upload,
  Slider,
  ColorPicker,
  Button,
  Card,
  Row,
  Col,
  Typography,
  Space,
  message,
  Divider,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { Demo } from "../../../../assets/images";
import {
  AddEventWatermark,
  GetEventDetailService,
} from "../../../../apis/events";
import { useParams } from "react-router-dom";
import { tostE, tostS } from "../../../../config/toastConfig";
import { watermarkPosition } from "../../../../utils/commonData";

const { Title, Text } = Typography;
const { Option } = Select;

const WatermarkComponent = () => {
  const { id } = useParams();

  const [uploadedFileName, setUploadedFileName] = useState("");
  const [uploadedBinary, setUploadedBinary] = useState(null);
  const previewImgRef = useRef(null);
  const [previewDims, setPreviewDims] = useState({ width: 0, height: 0 });
  const [eventData, setEventData] = useState({});
  const [loading, setLoading] = useState(false);

  // Validation schema using Yup
  const validationSchema = Yup.object({
    is_enable_watermark: Yup.boolean(),
    watermark_apply_on: Yup.string().required(
      "Please select where to apply watermark",
    ),
    watermark_logo: Yup.mixed().when("is_enable_watermark", {
      is: true,
      then: (schema) => schema.required("Please upload a watermark image"),
      otherwise: (schema) => schema.notRequired(),
    }),
    watermark_logo_opacity: Yup.number().min(0).max(100).required(),
    watermark_proportional: Yup.number().min(1).max(15).required(),
    watermark_position: Yup.string().required("Please select a position"),
  });

  // Initial values (mapped from server data if present)
  const initialValues = {
    event_id: id,
    is_enable_watermark:
      eventData && typeof eventData.is_enable_watermark !== "undefined"
        ? eventData.is_enable_watermark
          ? 1
          : 0
        : 0,
    watermark_apply_on:
      eventData?.watermark_apply_on === null
        ? "both"
        : eventData?.watermark_apply_on || "both",
    watermark_logo: eventData?.watermark_logo || "",
    // Server stores opacity as fraction (0-1), map to percent (0-100) for slider
    watermark_logo_opacity:
      typeof parseInt(eventData?.watermark_logo_opacity) === "number"
        ? parseInt(eventData?.watermark_logo_opacity) <= 1
          ? Math.round(eventData.watermark_logo_opacity * 100)
          : Math.round(eventData.watermark_logo_opacity)
        : 50,
    // Proportional size expected as 1-15 (% of background)
    watermark_proportional:
      typeof eventData?.watermark_proportional === "number"
        ? Math.min(
            15,
            Math.max(1, Math.round(eventData.watermark_proportional)),
          )
        : 5,
    watermark_position: eventData?.watermark_position || "bottom-right",
  };

  const getEventDetails = async () => {
    try {
      const res = await GetEventDetailService({ id });
      if (res.status === 200 && res.data.success) {
        setEventData(res.data.event);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    const formData = new FormData();
    formData.append("event_id", values.event_id);
    formData.append("is_enable_watermark", values.is_enable_watermark ? 1 : 0);
    formData.append("watermark_apply_on", values.watermark_apply_on);
    formData.append(
      "watermark_logo_opacity",
      values.watermark_logo_opacity / 100,
    );
    formData.append("watermark_proportional", values.watermark_proportional);
    formData.append("watermark_position", values.watermark_position);
    if (uploadedBinary) {
      formData.append("watermark_logo", uploadedBinary);
    }
    try {
      const response = await AddEventWatermark(formData);
      if (response.data.success === true) {
        tostS(response.data.message);
      }
    } catch (error) {
      tostE(error.response.data.message);

      console.error("Error applying watermark settings:", error);
      message.error("Failed to apply watermark settings. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (file, setFieldValue) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files!");
      return false;
    }

    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error("Image must be 2MB or smaller!");
      return false;
    }

    // Read file as binary (base64)
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      const img = new Image();
      img.onload = () => {
        if (img.width < 512 || img.height < 512) {
          message.error("Image dimensions must be at least 512×512 pixels.");
          setUploadedFileName("");
          return;
        }
        setFieldValue("watermark_logo", dataUrl);
        // Convert DataURL to Blob for binary form upload
        const dataURLToBlob = (url) => {
          const arr = url.split(",");
          const mimeMatch = arr[0].match(/:(.*?);/);
          const mime = mimeMatch && mimeMatch[1] ? mimeMatch[1] : "image/png";
          const bstr = atob(arr[1]);
          let n = bstr.length;
          const u8arr = new Uint8Array(n);
          while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
          }
          return new Blob([u8arr], { type: mime });
        };
        const blob = dataURLToBlob(dataUrl);
        setUploadedBinary(blob);
        setUploadedFileName(file.name || "");
        message.success("Image uploaded successfully");
      };
      img.onerror = () => {
        message.error("Invalid image file. Please try another image.");
        setUploadedFileName("");
        setUploadedBinary(null);
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);

    return false; // Prevent default upload
  };

  const uploadProps = (setFieldValue) => ({
    name: "watermark",
    showUploadList: false,
    beforeUpload: (file) => handleFileUpload(file, setFieldValue),
  });

  // Function to get watermark position styles
  const getWatermarkPositionStyles = (position) => {
    const baseStyles = {
      position: "absolute",
      padding: "8px 12px",
      borderRadius: "4px",
      pointerEvents: "none",
    };

    switch (position) {
      case "top-left":
        return {
          ...baseStyles,
          top: "10px",
          left: "10px",
        };
      case "top-right":
        return {
          ...baseStyles,
          top: "10px",
          right: "10px",
        };
      case "bottom-left":
        return {
          ...baseStyles,
          bottom: "10px",
          left: "10px",
        };
      case "bottom-right":
        return {
          ...baseStyles,
          bottom: "10px",
          right: "10px",
        };
      default:
        return {
          ...baseStyles,
          bottom: "10px",
          right: "10px",
        };
    }
  };

  const updatePreviewDims = () => {
    if (previewImgRef.current) {
      setPreviewDims({
        width: previewImgRef.current.clientWidth || 0,
        height: previewImgRef.current.clientHeight || 0,
      });
    }
  };

  useEffect(() => {
    updatePreviewDims();
    const onResize = () => updatePreviewDims();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    getEventDetails();
  }, []);

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue, errors, touched, handleSubmit }) => {
          return (
            <div>
              <Row gutter={24}>
                {/* Left Panel - Form Controls */}
                <Col xs={24} lg={12}>
                  <Card title="Watermark Settings" className="h-fit">
                    <Space
                      direction="vertical"
                      size="large"
                      style={{ width: "100%" }}
                    >
                      {/* Enable Watermark Toggle */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <Title level={5} style={{ margin: 0 }}>
                              Enable Watermark
                            </Title>
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                              Enable this option to add a watermark to all
                              images.
                            </Text>
                          </div>
                          <Switch
                            checked={values.is_enable_watermark}
                            onChange={(checked) =>
                              setFieldValue(
                                "is_enable_watermark",
                                checked ? 1 : 0,
                              )
                            }
                            size="default"
                          />
                        </div>
                      </div>

                      <Divider style={{ margin: "12px 0" }} />

                      {/* Watermark Apply To */}
                      <div>
                        <Text strong>Watermark Apply to</Text>
                        <Select
                          value={values.watermark_apply_on}
                          onChange={(value) =>
                            setFieldValue("watermark_apply_on", value)
                          }
                          style={{ width: "100%", marginTop: 8 }}
                          placeholder="Select application scope"
                        >
                          <Option value="both">Both</Option>
                          <Option value="view">View</Option>
                          <Option value="download">Download</Option>
                        </Select>
                        {errors.watermark_apply_on &&
                          touched.watermark_apply_on && (
                            <Text
                              type="danger"
                              style={{
                                fontSize: "12px",
                                display: "block",
                                marginTop: 4,
                              }}
                            >
                              {errors.watermark_apply_on}
                            </Text>
                          )}
                      </div>

                      {/* Watermark Image Upload */}
                      <div>
                        <Text strong>Watermark Image Upload</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          (Minimum 512×512 px, Maximum file size 2MB)
                        </Text>
                        <div style={{ marginTop: 8 }}>
                          <Upload {...uploadProps(setFieldValue)}>
                            <Button
                              icon={<UploadOutlined />}
                              style={{ marginRight: 8 }}
                            >
                              Choose File
                            </Button>
                          </Upload>
                          {uploadedFileName && (
                            <Text style={{ fontSize: "12px", marginLeft: 8 }}>
                              {uploadedFileName}
                            </Text>
                          )}
                        </div>
                        {errors.watermark_logo && touched.watermark_logo && (
                          <Text
                            type="danger"
                            style={{
                              fontSize: "12px",
                              display: "block",
                              marginTop: 4,
                            }}
                          >
                            {errors.watermark_logo}
                          </Text>
                        )}
                      </div>

                      {/* Watermark Opacity */}
                      <div>
                        <Text strong>Watermark Opacity</Text>
                        <div style={{ marginTop: 8 }}>
                          <Slider
                            min={0}
                            max={100}
                            value={values.watermark_logo_opacity}
                            onChange={(value) =>
                              setFieldValue("watermark_logo_opacity", value)
                            }
                            marks={{
                              0: "0",
                              100: "1",
                            }}
                            tooltip={{
                              formatter: (value) => `${value}%`,
                            }}
                          />
                        </div>
                      </div>

                      {/* Watermark Size */}
                      <div>
                        <Text strong>Watermark Size</Text>
                        <div style={{ marginTop: 8 }}>
                          <Slider
                            min={1}
                            max={15}
                            value={values.watermark_proportional}
                            onChange={(value) =>
                              setFieldValue("watermark_proportional", value)
                            }
                            marks={{
                              1: "1",
                              15: "15",
                            }}
                            tooltip={{
                              formatter: (value) => `${value}%`,
                            }}
                          />
                        </div>
                        {errors.watermark_proportional &&
                          touched.watermark_proportional && (
                            <Text
                              type="danger"
                              style={{
                                fontSize: "12px",
                                display: "block",
                                marginTop: 4,
                              }}
                            >
                              {errors.watermark_proportional}
                            </Text>
                          )}
                      </div>

                      {/* Position */}
                      <div>
                        <Text strong>Position</Text>
                        <Select
                          value={values.watermark_position}
                          onChange={(value) =>
                            setFieldValue("watermark_position", value)
                          }
                          style={{ width: "100%", marginTop: 8 }}
                          placeholder="-- Select --"
                        >
                          {watermarkPosition.map((item) => (
                            <Option key={item.value} value={item.value}>
                              {item.label}
                            </Option>
                          ))}
                        </Select>
                        {errors.watermark_position &&
                          touched.watermark_position && (
                            <Text
                              type="danger"
                              style={{
                                fontSize: "12px",
                                display: "block",
                                marginTop: 4,
                              }}
                            >
                              {errors.watermark_position}
                            </Text>
                          )}
                      </div>

                      {/* Submit Button */}
                      <div style={{ paddingTop: 16 }}>
                        <Button
                          type="primary"
                          size="large"
                          block
                          onClick={handleSubmit}
                          disabled={loading}
                        >
                          Apply Watermark Settings
                        </Button>
                      </div>
                    </Space>
                  </Card>
                </Col>

                {/* Right Panel - Preview */}
                <Col xs={24} lg={12}>
                  <Card title="Preview" className="h-fit">
                    <div style={{ position: "relative", textAlign: "center" }}>
                      <img
                        src={Demo}
                        alt="Preview"
                        style={{
                          width: "100%",
                          maxWidth: "800px",
                          height: "auto",
                          objectFit: "cover",
                          borderRadius: "8px",
                          border: "1px solid #d9d9d9",
                        }}
                        ref={previewImgRef}
                        onLoad={updatePreviewDims}
                      />
                      {values.is_enable_watermark ? (
                        <div
                          style={{
                            ...getWatermarkPositionStyles(
                              values.watermark_position,
                            ),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {values.watermark_logo ? (
                            <img
                              src={values.watermark_logo}
                              alt="Watermark"
                              style={{
                                width: previewDims.width
                                  ? `${(values.watermark_proportional / 50) * previewDims.width}px`
                                  : "60px",
                                height: "auto",
                                objectFit: "contain",
                                opacity: values.watermark_logo_opacity / 100,
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                color: "white",
                                fontWeight: "bold",
                                fontSize: previewDims.width
                                  ? `${Math.max(
                                      12,
                                      ((values.watermark_proportional / 100) *
                                        previewDims.width) /
                                        10,
                                    )}px`
                                  : "16px",
                                opacity: values.watermark_logo_opacity / 100,
                                textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
                              }}
                            >
                              WATERMARK
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>

                    <div
                      style={{
                        marginTop: 16,
                        padding: "12px",
                        backgroundColor: "#f5f5f5",
                        borderRadius: "6px",
                      }}
                    >
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        <strong>Current Settings:</strong>
                        <br />
                        Position: {values.watermark_position} | Opacity:{" "}
                        {values.watermark_logo_opacity}% | Size:{" "}
                        {values.watermark_proportional}%
                      </Text>
                    </div>
                  </Card>
                </Col>
              </Row>
            </div>
          );
        }}
      </Formik>
    </div>
  );
};

export default WatermarkComponent;
